import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { ProductType } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';

import { BucketContext, CatalogueContext, S3Item } from 'contexts';

import { routes } from 'data/routes';

const useProductItem = (data: ProductType, asSubProduct?: boolean) => {
  const { getUrl } = useContext(BucketContext);
  const { isOrderModalVisible, isOrderModalVisibleSet, subProductsSet } =
    useContext(CatalogueContext);
  const { warehouse } = useAppSelector((state) => state.settings);

  const { genre, category } = useParams();
  const [searchParams, searchParamsSet] = useSearchParams(); // eslint-disable-line

  const [image, imageSet] = useState<string>('');
  const [isLoading, isLoadingSet] = useState<boolean>(false);
  const [isModalVisible, isModalVisibleSet] = useState<boolean>(false);

  const isCatalogue = warehouse.code === '1';

  const { t } = useTranslation();

  const routeParams = {
    genre: genre!,
    category: category!,
    id: data.id,
    itemCode: data.itemCode,
    mainFabricCode: data.mainFabricCode,
    itemColorCode: data.colorCode || data.itemColorCode,
    genderCodeAndGenderDesc: data.genderCodeAndGenderDesc,
    styleCodeAndStyleDesc: data.styleCodeAndStyleDesc,
  };

  const path = asSubProduct
    ? routes.productDetails(routeParams)
    : routes.colorsList(routeParams);

  const isAvailable = isCatalogue || (data?.quantity && data.quantity > 0);

  const quickOrder = () => {
    if (data) {
      const params = {
        itemCode: data.itemCode,
        mainFabricCode: data.mainFabricCode,
        colorCode: data.colorCode,
        styleCodeAndStyleDesc: data.styleCodeAndStyleDesc,
        genderCodeAndGenderDesc: data.genderCodeAndGenderDesc,
      };

      subProductsSet([]);
      searchParamsSet(params);

      isOrderModalVisibleSet(true);
    }
  };

  useEffect(() => {
    if (!data?.imageName) return;
    isLoadingSet(true);
    getUrl(`catalogue/products${data.imageName}`)
      .then((res: S3Item) => {
        if (!res) {
          isLoadingSet(false);
          return;
        }

        if (!res.url) {
          isLoadingSet(false);
          return;
        }
        imageSet(res.url);
        isLoadingSet(false);
      })
      .catch((e: Error) => {
        imageSet('');
        isLoadingSet(false);
      });
  }, [data]);

  return {
    image,
    isLoading,
    isModalVisible,
    isModalVisibleSet,
    t,
    path,
    isAvailable,
    isOrderModalVisible,
    isOrderModalVisibleSet,
    quickOrder,
    routeParams,
    routes,
  };
};

export { useProductItem };
